import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";
import { getDataRows } from "src/utils/ScoresGrid/getDataRows";

import styles from "./MyResults.module.scss";

interface IResultRow {
    scoreId: string;
    callName: string;
    showType: string;
    sanctioningClub?: string;
    showName: string;
    showDate: string;
    eventHandle?: string;
    eventId?: string;
    element: string;
    level: string;
    status?: string;
    result: string;
    // Detailed stats (populated by getDataRows / getShowSpecificData)
    score?: number | string;
    time?: number | string;
    searchTime?: number | string;
    finalTime?: number | string;
    placement?: string;
    faults?: number | string;
    faultCount?: number | string;
    totalDeductions?: number | string;
    feet?: number | string;
    inches?: number | string;
    highestWeight?: number | string;
    percentPulled?: number | string;
}

const MyResults: React.FC = () => {
    const [results, setResults] = useState<IResultRow[]>([]);

    useEffect(() => {
        apiService.getOwnerScores().then((scores: IScore[]) => {
            const rows = getDataRows(
                scores
                    .filter(s => s.show_type !== Constants.show_type.conformation)
                    .sort((a, b) => new Date(b.show_date).getTime() - new Date(a.show_date).getTime())
                    .slice(0, 5)
            ) as IResultRow[];
            setResults(rows);
        }).catch(() => {
            // silently fail — sidebar is non-critical
        });
    }, []);

    if (results.length === 0) return null;

    const getResultClass = (result: string): string => {
        if (!result) return "";
        const r = result.toLowerCase();
        if (r === "qualified" || r === "passed" || r === "pass" || r === "complete") return styles.resultPass;
        if (r === "nq" || r === "failed" || r === "fail" || r === "disqualified") return styles.resultFail;
        return styles.resultNeutral;
    };

    const getStatusClass = (status: string): string => {
        const s = status.toLowerCase();
        if (s === "complete") return styles.statusComplete;
        if (s === "pending") return styles.statusPending;
        return styles.statusDefault;
    };

    const buildStats = (row: IResultRow): { label: string; value: string }[] => {
        const stats: { label: string; value: string }[] = [];

        if (row.score !== undefined && row.score !== "" && row.score !== null) {
            stats.push({ label: "Score", value: String(row.score) });
        }
        if (row.time !== undefined && row.time !== "" && row.time !== null && row.time !== "N/A") {
            stats.push({ label: "Time", value: String(row.time) });
        } else if (row.searchTime !== undefined && row.searchTime !== "" && row.searchTime !== null) {
            stats.push({ label: "Time", value: String(row.searchTime) });
        } else if (row.finalTime !== undefined && row.finalTime !== "" && row.finalTime !== null) {
            stats.push({ label: "Time", value: String(row.finalTime) });
        }
        if (row.placement !== undefined && row.placement !== "" && row.placement !== null && row.placement !== "N/A") {
            stats.push({ label: "Place", value: String(row.placement) });
        }
        if (row.faults !== undefined && row.faults !== "" && row.faults !== null) {
            stats.push({ label: "Faults", value: String(row.faults) });
        } else if (row.faultCount !== undefined && row.faultCount !== "" && row.faultCount !== null) {
            stats.push({ label: "Faults", value: String(row.faultCount) });
        }
        if (row.totalDeductions !== undefined && row.totalDeductions !== "" && row.totalDeductions !== null) {
            stats.push({ label: "Deductions", value: String(row.totalDeductions) });
        }
        if (row.feet !== undefined && row.feet !== "" && row.feet !== null) {
            const dist = row.inches !== undefined && row.inches !== "" ? `${row.feet}' ${row.inches}"` : `${row.feet}'`;
            stats.push({ label: "Distance", value: dist });
        }
        if (row.highestWeight !== undefined && row.highestWeight !== "" && row.highestWeight !== null) {
            stats.push({ label: "Weight", value: String(row.highestWeight) });
        }
        if (row.percentPulled !== undefined && row.percentPulled !== "" && row.percentPulled !== null) {
            stats.push({ label: "% Pulled", value: String(row.percentPulled) });
        }

        return stats;
    };

    return (
        <div className={styles.MyResults}>
            <div className={styles.title}>Recent Results</div>
            <ul className={styles.list}>
                {results.map((row) => {
                    const stats = buildStats(row);
                    return (
                        <li key={row.scoreId} className={styles.item}>
                            <div className={styles.inner}>
                                <div className={styles.header}>
                                    <span className={styles.dogName}>{row.callName}</span>
                                    <div className={styles.headerRight}>
                                        {row.status && row.status.toLowerCase() !== "complete" && (
                                            <span className={`${styles.status} ${getStatusClass(row.status)}`}>
                                                {row.status}
                                            </span>
                                        )}
                                        {row.result && row.result.toLowerCase() !== "no result" && (
                                            <span className={`${styles.result} ${getResultClass(row.result)}`}>
                                                {row.result}
                                            </span>
                                        )}
                                        {row.sanctioningClub && (
                                            <span className={styles.sanctioningClub}>{row.sanctioningClub}</span>
                                        )}
                                        <span className={styles.showType}>{row.showType}</span>
                                    </div>
                                </div>
                                <div className={styles.showLine}>
                                    <span className={styles.showName}>{row.showName}</span>
                                    <span className={styles.showDate}>{row.showDate}</span>
                                </div>
                                {(row.element || row.level) && (
                                    <div className={styles.detailLine}>
                                        {row.element && <span className={styles.detail}>{row.element}</span>}
                                        {row.level && row.level !== row.element && (
                                            <span className={styles.detail}>{row.level}</span>
                                        )}
                                    </div>
                                )}
                                {stats.length > 0 && (
                                    <div className={styles.statsRow}>
                                        {stats.map((s) => (
                                            <div key={s.label} className={styles.stat}>
                                                <span className={styles.statLabel}>{s.label}</span>
                                                <span className={styles.statValue}>{s.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className={styles.tileFooter}>
                                    {(row.eventHandle || row.eventId) && (
                                        <Link
                                            to={`/e/${row.eventHandle ?? row.eventId}`}
                                            className={styles.viewEvent}
                                        >
                                            {moment(row.showDate, "MM/DD/YYYY").isBefore(moment(), "day")
                                                ? "View Event Summary →"
                                                : "View Event →"}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
            <div className={styles.viewAll}>
                <Link to="/account?t=2">View All Results →</Link>
            </div>
        </div>
    );
};

export default MyResults;
